import { Component } from "react";

/**
 * App-wide error boundary. Catches any uncaught render-time error in the
 * React tree below it and renders a recovery card with a "Reload" CTA.
 *
 * Without this, a single defensive-coding miss (e.g. `someObj.foo.length`
 * where `foo` is unexpectedly undefined) takes down the entire app —
 * the user sees a fully blank page because React unmounts the root.
 *
 * The boundary is intentionally simple — no third-party error reporting,
 * no toast — just a calm Coral-styled card that tells the user something
 * went wrong and offers a one-click reload. The full stack is logged to
 * the console so devs can still diagnose from DevTools.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Unknown render error" };
  }

  componentDidCatch(err, info) {
    // Surface to the browser console so the existing devtools workflow keeps
    // working. Don't ship this anywhere — keep the boundary dependency-free.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          background: "var(--color-bg-siphon, #100030)",
          fontFamily: "var(--font-family, 'Outfit', sans-serif)",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            padding: "2rem",
            background: "var(--color-bg-hemocyanin, #180048)",
            border: "1px solid rgba(255, 62, 104, 0.4)",
            borderRadius: "var(--border-radius, 12px)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          }}
        >
          <p
            style={{
              fontSize: "0.72rem",
              color: "var(--color-error-light, #FF809B)",
              fontWeight: 500,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: "0.5rem",
            }}
          >
            Something broke
          </p>
          <h1
            style={{
              fontSize: "1.6rem",
              color: "var(--color-bg-ice, #F0FFFF)",
              marginBottom: "0.75rem",
              fontWeight: 500,
              letterSpacing: "-0.015em",
              lineHeight: 1.25,
            }}
          >
            We hit an unexpected error rendering this page.
          </h1>
          <p
            style={{
              color: "var(--color-bg-purple-haze, #A49FC8)",
              fontSize: "0.92rem",
              lineHeight: 1.55,
              marginBottom: "1.5rem",
              fontWeight: 300,
            }}
          >
            Try reloading. If it keeps happening, drop the error message below to your friendly engineer.
          </p>
          {this.state.message && (
            <pre
              data-testid="error-boundary-message"
              style={{
                background: "rgba(255, 62, 104, 0.08)",
                border: "1px solid rgba(255, 62, 104, 0.25)",
                borderRadius: "var(--border-radius, 12px)",
                color: "var(--color-error-light, #FF809B)",
                padding: "0.85rem 1rem",
                fontSize: "0.78rem",
                fontFamily: "'JetBrains Mono', monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginBottom: "1.5rem",
              }}
            >
              {this.state.message}
            </pre>
          )}
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload-btn"
            style={{
              width: "100%",
              padding: "0.8rem 1rem",
              borderRadius: "var(--border-radius, 12px)",
              border: "2px solid transparent",
              background: "var(--color-primary-500, #F050F8)",
              color: "var(--color-bg-siphon, #100030)",
              fontWeight: 500,
              fontSize: "0.95rem",
              cursor: "pointer",
              fontFamily: "var(--font-family, 'Outfit', sans-serif)",
              transition: "background 200ms cubic-bezier(0.4,0,0.2,1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-primary-600, #FF2FFB)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--color-primary-500, #F050F8)";
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
